import type { SessionUser } from '@accounts/shared';

import { STR } from '../i18n';

/**
 * THE ONE DECLARATION — the client half of the three-layer law.
 *
 * Nav visibility, the route guard, and which API reads a page is allowed to issue ALL derive
 * from this table and nothing else. Two places that could disagree about who may enter a
 * surface is exactly the bug this exists to prevent, so there is no second list of admin
 * paths, no second `isAdmin` check scattered through a component, and no nav array.
 *
 * DEFAULT-DENY. Every route this app serves has an entry here carrying an explicit gate —
 * including the deliberately open ones. A path with no entry does not render a shell: it
 * falls through to the not-found surface. `SurfaceId` is derived from this array and the
 * element map in App.tsx is typed `Record<SurfaceId, ...>`, so a surface declared here
 * without a screen fails the typecheck rather than shipping as an orphan.
 *
 * This is affordance only. The server declares the same gates in its own ROUTE_TABLE and
 * enforces them independently; anything bypassed here is refused there.
 */

export type Capability =
  /** No credential required. */
  | 'public'
  /** A valid session. Subject to the must-change-password wall. */
  | 'authenticated'
  /** A valid session, exempt from the wall — the wall's own screen. */
  | 'authenticated-open'
  /** A valid session AND `isAdmin`. */
  | 'admin'
  /**
   * A valid session AND (`isAdmin` OR `isHr` OR `isIt`) — the server's `user-manager`
   * gate. HR and IT enter the same doors for now; the separate flags let IT narrow later.
   */
  | 'user-manager'
  /**
   * A valid session AND (`isAdmin` OR the Settings → Access "See Matrix" switch for one of
   * the person's roles), read from `access`, which the server resolves (SIR-7864).
   */
  | 'matrix-viewer'
  /** As `matrix-viewer`, for the "See Catalog" switch. View only: Catalog writes stay admin's. */
  | 'catalog-viewer';

/**
 * A surface's role as a destination other surfaces navigate to. Each value appears at most
 * once in the table — two rows claiming the same role is two answers to one question.
 */
export type Destination =
  /** Where EVERY signed-in person lands after sign-in. Exactly one surface carries this. */
  | 'landing'
  /** The admin panel's entry point — where the launcher's "Admin panel" action goes. */
  | 'panel-entry';

export interface Surface {
  readonly id: string;
  readonly path: string;
  readonly capability: Capability;
  /** Rail entry, or null when the surface has no navigation presence. */
  readonly nav: { readonly label: string; readonly letter: string } | null;
  /** Admin header, or null for surfaces that render outside the admin chrome. */
  readonly header: {
    readonly title: (brand: string) => string;
    readonly subtitle: (brand: string) => string;
  } | null;
  /**
   * What this surface is a destination FOR. Declared HERE, beside the gate and the nav
   * entry, so a destination can never disagree with who may enter it. Null on every surface
   * nothing navigates to by role.
   */
  readonly destination: Destination | null;
}

const constant =
  (value: string) =>
  (): string =>
    value;

export const SURFACES = [
  {
    id: 'login',
    path: '/login',
    capability: 'public',
    nav: null,
    header: null,
    destination: null,
  },
  {
    /*
     * The sign-in challenge — the login page one step later. `public` is DELIBERATE and is
     * the same shape as the login surface itself: the person holds only the '2fa-pending'
     * cookie, which `GET /api/auth/me` structurally refuses, so to this client they look
     * signed out. The screen is affordance; the server's `totp-challenge` gate is the
     * enforcement — without the pending cookie every submission is a uniform 401.
     */
    id: 'twoFactorChallenge',
    path: '/login/two-step',
    capability: 'public',
    nav: null,
    header: null,
    destination: null,
  },
  {
    /*
     * Enrollment — scan & confirm. `public` for the same reason as the challenge (a person
     * held at login by the operator's requirement looks signed out to this client), and
     * because the SAME screen serves the signed-in volunteer arriving from Security. The
     * server's `totp-enroll` gate (full session OR '2fa-pending') is the enforcement.
     */
    id: 'twoFactorSetup',
    path: '/two-step/setup',
    capability: 'public',
    nav: null,
    header: null,
    destination: null,
  },
  {
    id: 'changePassword',
    path: '/change-password',
    capability: 'authenticated-open',
    nav: null,
    header: null,
    destination: null,
  },
  {
    id: 'home',
    path: '/',
    capability: 'authenticated',
    nav: null,
    header: null,
    destination: 'landing',
  },
  {
    /** Self-service security — two-step management, reached from the account menu. */
    id: 'security',
    path: '/account/security',
    capability: 'authenticated',
    nav: null,
    header: null,
    destination: null,
  },
  {
    id: 'users',
    path: '/admin/users',
    // `user-manager`, matching the server's gate on the users list (HR sees Users).
    capability: 'user-manager',
    nav: { label: STR.surfaces.users.navLabel, letter: STR.surfaces.users.navLetter },
    header: {
      title: constant(STR.surfaces.users.title),
      subtitle: constant(STR.surfaces.users.subtitle),
    },
    destination: 'panel-entry',
  },
  {
    id: 'applications',
    path: '/admin/applications',
    capability: 'admin',
    nav: {
      label: STR.surfaces.applications.navLabel,
      letter: STR.surfaces.applications.navLetter,
    },
    header: {
      title: constant(STR.surfaces.applications.title),
      subtitle: STR.surfaces.applications.subtitle,
    },
    destination: null,
  },
  {
    id: 'grants',
    path: '/admin/grants',
    capability: 'admin',
    nav: { label: STR.surfaces.grants.navLabel, letter: STR.surfaces.grants.navLetter },
    header: {
      title: constant(STR.surfaces.grants.title),
      subtitle: constant(STR.surfaces.grants.subtitle),
    },
    destination: null,
  },
  /*
   * THE ACCESS HUB (SIR-7424, FINAL demo R3-2): ONE rail entry, its sections below it as
   * surfaces of their own so the guard and the gates stay in this one table. Only the hub's
   * Overview carries the rail entry; the rail marks it current on any of its sections.
   * Overview and Queue are `user-manager` (HR works its scope there). Catalog and Matrix are
   * admin's, and HR's or IT's only where the board's Settings → Access switch says so
   * (SIR-7864); the catalog writes stay admin's either way.
   */
  {
    id: 'access',
    path: '/admin/access',
    capability: 'user-manager',
    nav: { label: STR.surfaces.access.navLabel, letter: STR.surfaces.access.navLetter },
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.subtitle),
    },
    destination: null,
  },
  {
    id: 'accessQueue',
    path: '/admin/access/queue',
    capability: 'user-manager',
    nav: null,
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.queueSubtitle),
    },
    destination: null,
  },
  {
    id: 'accessCatalog',
    path: '/admin/access/catalog',
    capability: 'catalog-viewer',
    nav: null,
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.catalogSubtitle),
    },
    destination: null,
  },
  {
    id: 'accessMatrix',
    path: '/admin/access/matrix',
    capability: 'matrix-viewer',
    nav: null,
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.matrixSubtitle),
    },
    destination: null,
  },
  {
    /** The IT step (spec row 15). Admin and HR both see every IT email (SIR-7690). */
    id: 'accessItEmails',
    path: '/admin/access/it-emails',
    capability: 'user-manager',
    nav: null,
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.itEmailsSubtitle),
    },
    destination: null,
  },
  {
    /** The answer emails in the outbox (board, SIR-7687 c2b7ba01). HR and IT read it (SIR-7864). */
    id: 'accessEmails',
    path: '/admin/access/emails',
    capability: 'user-manager',
    nav: null,
    header: {
      title: constant(STR.surfaces.access.title),
      subtitle: constant(STR.surfaces.access.emailsSubtitle),
    },
    destination: null,
  },
  {
    id: 'audit',
    path: '/admin/audit',
    capability: 'admin',
    nav: { label: STR.surfaces.audit.navLabel, letter: STR.surfaces.audit.navLetter },
    header: {
      title: constant(STR.surfaces.audit.title),
      subtitle: constant(STR.surfaces.audit.subtitle),
    },
    destination: null,
  },
  {
    id: 'sessions',
    path: '/admin/sessions',
    capability: 'admin',
    nav: { label: STR.surfaces.sessions.navLabel, letter: STR.surfaces.sessions.navLetter },
    header: {
      title: constant(STR.surfaces.sessions.title),
      subtitle: constant(STR.surfaces.sessions.subtitle),
    },
    destination: null,
  },
  {
    id: 'announcements',
    path: '/admin/announcements',
    capability: 'admin',
    nav: {
      label: STR.surfaces.announcements.navLabel,
      letter: STR.surfaces.announcements.navLetter,
    },
    header: {
      title: constant(STR.surfaces.announcements.title),
      subtitle: constant(STR.surfaces.announcements.subtitle),
    },
    destination: null,
  },
  {
    id: 'settings',
    path: '/admin/settings',
    capability: 'admin',
    nav: { label: STR.surfaces.settings.navLabel, letter: STR.surfaces.settings.navLetter },
    header: {
      title: constant(STR.surfaces.settings.title),
      subtitle: STR.surfaces.settings.subtitle,
    },
    destination: null,
  },
  {
    /** Settings is a hub like Access (SIR-7804): `settings` above is its General section. */
    id: 'settingsOperations',
    path: '/admin/settings/operations',
    capability: 'admin',
    nav: null,
    header: {
      title: constant(STR.surfaces.settings.title),
      subtitle: constant(STR.surfaces.settings.operationsSubtitle),
    },
    destination: null,
  },
  {
    id: 'settingsEmail',
    path: '/admin/settings/email',
    capability: 'admin',
    nav: null,
    header: {
      title: constant(STR.surfaces.settings.title),
      subtitle: constant(STR.surfaces.settings.emailSubtitle),
    },
    destination: null,
  },
  {
    id: 'settingsAccess',
    path: '/admin/settings/access',
    capability: 'admin',
    nav: null,
    header: {
      title: constant(STR.surfaces.settings.title),
      subtitle: constant(STR.surfaces.settings.accessSubtitle),
    },
    destination: null,
  },
] as const satisfies readonly Surface[];

export type SurfaceId = (typeof SURFACES)[number]['id'];

export const LOGIN_PATH = '/login';
export const CHANGE_PASSWORD_PATH = '/change-password';
/*
 * There is deliberately no `HOME_PATH` constant. It used to sit here at '/', which was a
 * SECOND statement of the landing destination that merely happened to agree with the table's
 * — value-coincident, not derived, and free to disagree the day the landing surface moves.
 * Every navigation to the landing goes through `landingPath()` below, which reads the one
 * declaration. `LOGIN_PATH` and `CHANGE_PASSWORD_PATH` stay because neither is a landing:
 * they are the two surfaces the guard redirects TO by name, and both are declared above.
 */

/** THE gate test. Every guard and every nav entry asks this one question. */
export function canEnter(user: SessionUser | null, capability: Capability): boolean {
  switch (capability) {
    case 'public':
      return true;
    case 'authenticated':
    case 'authenticated-open':
      return user !== null;
    case 'admin':
      return user !== null && user.isAdmin;
    case 'user-manager':
      return user !== null && (user.isAdmin || user.isHr || user.isIt);
    case 'matrix-viewer':
      return user !== null && (user.isAdmin || user.access.seeMatrix);
    case 'catalog-viewer':
      return user !== null && (user.isAdmin || user.access.seeCatalog);
  }
}

export function surfaceById(id: SurfaceId): Surface {
  const found = SURFACES.find((surface) => surface.id === id);
  // Unreachable: `id` is derived from the array itself.
  if (found === undefined) throw new Error(`Unknown surface: ${id}`);
  return found;
}

/**
 * THE landing surface — ONE rule, for everybody. Derived from THIS table, never a second list.
 *
 * EVERYONE lands on the launcher after signing in, administrators included. One destination
 * cannot disagree with itself: the moment landing branches on who you are, "where do I go
 * after signing in" has two answers and the two drift. An administrator still reaches the
 * panel deliberately, through the `panel-entry` surface below — a declared door they choose
 * to walk through, not a different corridor they are routed down at sign-in.
 *
 * IT TAKES NO ACTOR, ON PURPOSE. A role-dependent landing cannot be reintroduced without
 * changing this signature and every call site, which makes it a visible decision rather than
 * a quiet `if` somebody adds inside the body.
 */
export function landingSurface(): Surface {
  const landing = SURFACES.find((surface) => surface.destination === 'landing');
  // A table with no landing at all must fail loudly rather than silently pick a surface.
  if (landing === undefined) throw new Error('No landing surface is declared');
  return landing;
}

/** Its path — what every post-sign-in navigation actually needs. */
export function landingPath(): string {
  return landingSurface().path;
}

/**
 * THE admin panel's door, for one actor — the launcher's "Admin panel" entry and the panel's
 * own way back are both drawn from this, never from a hardcoded '/admin/users'.
 *
 * It tests the entry surface's OWN declared capability, so the affordance renders IFF the
 * actor may actually enter what it points at — the three-layer law, satisfied by derivation
 * rather than by a hand-written `user.isAdmin` that would be free to disagree with the gate.
 *
 * Null when the table declares no panel entry at all: unlike the landing, a suite with no
 * admin panel is a coherent table, so this is an absent door rather than a broken one.
 */
export function panelEntrySurface(user: SessionUser | null): Surface | null {
  const entry = SURFACES.find((surface) => surface.destination === 'panel-entry');
  if (entry === undefined) return null;
  return canEnter(user, entry.capability) ? entry : null;
}

/** The surfaces whose sections live under their own path: Access, and Settings (SIR-7804). */
const HUB_SURFACES: readonly SurfaceId[] = ['access', 'settings'];

/**
 * The rail entry a path belongs to: its own surface, or — for a section of a hub — the
 * hub's entry. Derived from the paths in this table, never a second list of sections.
 */
export function railOwnerPath(pathname: string): string {
  for (const id of HUB_SURFACES) {
    const hub = surfaceById(id).path;
    if (pathname.startsWith(`${hub}/`)) return hub;
  }
  return pathname;
}

/** The rail's entries — derived, never a separate list. */
export function navSurfaces(user: SessionUser | null): readonly Surface[] {
  return SURFACES.filter((surface) => surface.nav !== null && canEnter(user, surface.capability));
}
